import { useCallback, useRef } from 'react';
import { cn } from '@/lib/utils';

interface ResizeHandleProps {
  direction: 'horizontal' | 'vertical';
  onResize: (delta: number) => void;
  className?: string;
}

export default function ResizeHandle({
  direction,
  onResize,
  className,
}: ResizeHandleProps) {
  const isDragging = useRef(false);
  const lastPos = useRef(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      lastPos.current =
        direction === 'horizontal' ? e.clientX : e.clientY;

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging.current) return;
        const currentPos =
          direction === 'horizontal' ? e.clientX : e.clientY;
        const delta = currentPos - lastPos.current;
        lastPos.current = currentPos;
        onResize(delta);
      };

      const handleMouseUp = () => {
        isDragging.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor =
        direction === 'horizontal' ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
    },
    [direction, onResize]
  );

  return (
    <div
      className={cn(
        'group flex items-center justify-center z-10',
        direction === 'horizontal'
          ? 'w-1 cursor-col-resize hover:bg-blue-500/50 active:bg-blue-500/70'
          : 'h-1 cursor-row-resize hover:bg-blue-500/50 active:bg-blue-500/70',
        className
      )}
      onMouseDown={handleMouseDown}
    >
      <div
        className={cn(
          'rounded-full bg-gray-600 opacity-0 group-hover:opacity-100 transition-opacity',
          direction === 'horizontal' ? 'w-0.5 h-8' : 'h-0.5 w-8'
        )}
      />
    </div>
  );
}
